import { LayoutContainerModel } from "../models/containers/layout/LayoutContainerModel";
import { CountryModel } from "../models/locations/CountryModel";
import { StateModel } from "../models/locations/StateModel";
import { CityModel } from "../models/locations/CityModel";

import { LIST_CACHE_LIMIT } from "../config";
import { LOCATION_CACHE_LIMIT } from "../config";
import { DEPENDENT_CONTENT_CACHE_LIMIT } from "../config";


type ListKey = string;
type GetKey = string;

class LayoutsCacheStore {
    readonly listCache = new Map<ListKey, LayoutContainerModel[]>();
    readonly listPending = new Map<ListKey, Promise<LayoutContainerModel[] | undefined>>();

    readonly getCache = new Map<GetKey, LayoutContainerModel>();
    readonly getPending = new Map<GetKey, Promise<LayoutContainerModel | undefined>>();

    // Limpa as entradas mais antigas do cache de listas (FIFO simples)
    private keepListCacheWithinLimit(): void {
        while (this.listCache.size > LIST_CACHE_LIMIT) {
            const oldestKey = this.listCache.keys().next().value as ListKey | undefined;
            if (!oldestKey) break;
            this.listCache.delete(oldestKey);
        }
    }

    setListCache(key: ListKey, value: LayoutContainerModel[]): void {
        this.listCache.set(key, value);
        this.keepListCacheWithinLimit();
    }
}

type CountriesKey = string;
type StatesKey = string;
type CitiesKey = string;

class LocationsCacheStore {
    readonly countriesCache = new Map<CountriesKey, CountryModel[]>();
    readonly countriesPending = new Map<CountriesKey, Promise<CountryModel[] | undefined>>();

    readonly statesCache = new Map<StatesKey, StateModel[]>();
    readonly statesPending = new Map<StatesKey, Promise<StateModel[] | undefined>>();

    readonly citiesCache = new Map<CitiesKey, CityModel[]>();
    readonly citiesPending = new Map<CitiesKey, Promise<CityModel[] | undefined>>();

    private keepCacheWithinLimit<TKey, TValue>(cache: Map<TKey, TValue>): void {
        while (cache.size > LOCATION_CACHE_LIMIT) {
            const oldestKey = cache.keys().next().value as TKey | undefined;
            if (oldestKey === undefined) break;
            cache.delete(oldestKey);
        }
    }

    setCountriesCache(key: CountriesKey, value: CountryModel[]): void {
        this.countriesCache.set(key, value);
        this.keepCacheWithinLimit(this.countriesCache);
    }

    setStatesCache(key: StatesKey, value: StateModel[]): void {
        this.statesCache.set(key, value);
        this.keepCacheWithinLimit(this.statesCache);
    }

    setCitiesCache(key: CitiesKey, value: CityModel[]): void {
        this.citiesCache.set(key, value);
        this.keepCacheWithinLimit(this.citiesCache);
    }
}

type AssetCacheKey = string;

class AssetsCacheStore {
    readonly dependentContentCache = new Map<AssetCacheKey, any>();
    readonly dependentContentPending = new Map<AssetCacheKey, Promise<any | undefined>>();

    readonly standaloneAssetUriCache = new Map<AssetCacheKey, string>();
    readonly dependentAssetUriCache = new Map<AssetCacheKey, string>();

    private keepCacheWithinLimit<T>(cache: Map<AssetCacheKey, T>): void {
        while (cache.size > DEPENDENT_CONTENT_CACHE_LIMIT) {
            const oldestKey = cache.keys().next().value as AssetCacheKey | undefined;
            if (!oldestKey) break;
            cache.delete(oldestKey);
        }
    }

    setStandaloneAssetUri(key: AssetCacheKey, uri: string): void {
        this.standaloneAssetUriCache.set(key, uri);
        this.keepCacheWithinLimit(this.standaloneAssetUriCache);
    }

    setDependentAssetUri(key: AssetCacheKey, uri: string): void {
        this.dependentAssetUriCache.set(key, uri);
        this.keepCacheWithinLimit(this.dependentAssetUriCache);
    }

    setDependentContent(key: AssetCacheKey, content: any): void {
        this.dependentContentCache.set(key, content);
        this.keepCacheWithinLimit(this.dependentContentCache);
    }
}

export const layoutsCacheStore = new LayoutsCacheStore();
export const locationsCacheStore = new LocationsCacheStore();
export const assetsCacheStore = new AssetsCacheStore();